<template>
  <div :class="['form-input-amount-withdrawal', { 'form-input-amount-withdrawal_error': isAmountWithdrawError }]">
    <form-input-number
      v-model:value="localAmountValue"
      :label="label"
      :label-right="rightLabel"
      name="withdrawSum"
      :min="amountMin.amount"
      :max="withdrawalBalance.amount"
      :currency="amountMin.currency"
      :btn-name="getContent(layoutData, defaultLocaleLayoutData, 'buttons.max')"
      @btn-click="setMaxAmount"
    />

    <slider-amount-step
      class="form-input-amount-withdrawal__step"
      :initialPercentage="initialPercentage"
      @update="onSelectPercent"
    />

    <atomic-warning v-if="withdrawalWarningHtml">
      <div v-html="withdrawalWarningHtml" />
    </atomic-warning>
  </div>
</template>

<script setup lang="ts">
  import type { IAccount } from '@skeleton/core/types';
  import DOMPurify from 'isomorphic-dompurify';

  const props = defineProps<{
    amountMin: { amount: number; currency: string };
    amountMax: { amount: number; currency: string };
    label: string;
    activeAccount?: IAccount;
    method?: string;
    modelValue?: string | number;
  }>();

  const emit = defineEmits<{
    'update:modelValue': [value: string | number | undefined];
  }>();

  const { getContent, formatBalance } = useProjectMethods();
  const { popupsData, defaultLocalePopupsData, layoutData, defaultLocaleLayoutData } = storeToRefs(useGlobalStore());

  const localAmountValue = ref<string | number | undefined>(props.modelValue);

  const withdrawalBalance = computed(() => {
    return formatBalance(props.activeAccount?.currency, props.activeAccount?.withdrawalBalance);
  });

  const isAmountWithdrawError = computed(() => {
    if (!props.modelValue || !withdrawalBalance.value.currency) return false;
    const amount = Number(props.modelValue);
    return amount < props.amountMin.amount || amount > withdrawalBalance.value.amount;
  });

  const rightLabel = computed(() => {
    const minContent = getContent(popupsData.value, defaultLocalePopupsData.value, 'wallet.min');
    const maxContent = getContent(popupsData.value, defaultLocalePopupsData.value, 'wallet.max');
    const minAmountContent = `${minContent}: ${props.amountMin.amount} ${props.amountMin.currency}`;
    const maxAmountContent = `${maxContent}: ${withdrawalBalance.value.amount} ${withdrawalBalance.value.currency}`;

    return `${minAmountContent}.${maxAmountContent}`;
  });

  const initialPercentage = computed(() => {
    const percentage = (Number(localAmountValue.value) * 100) / Number(withdrawalBalance.value.amount);
    return Math.min(Math.max(percentage, 0), 100);
  });

  const setMaxAmount = () => {
    if (!withdrawalBalance.value.amount) return;
    localAmountValue.value = withdrawalBalance.value.amount;
  };

  const onSelectPercent = (percent: number): void => {
    if (!withdrawalBalance.value.amount) return;

    if (percent === 100) {
      // Для 100% используем полное значение баланса без округления
      localAmountValue.value = withdrawalBalance.value.amount;
      return;
    }

    const calculatedAmount = Number(((withdrawalBalance.value.amount * percent) / 100).toFixed(2));
    localAmountValue.value = Math.max(calculatedAmount, props.amountMin.amount);
  };

  const withdrawalWarningHtml = computed(() => {
    if (props.method?.includes('finteqhub')) {
      const html = getContent(popupsData.value, defaultLocalePopupsData.value, 'wallet.withdraw.finteqhubInfo');
      return html ? DOMPurify.sanitize(html, { ADD_ATTR: ['target'] }) : DOMPurify.sanitize('<p></p>');
    }
    return '';
  });

  watch(
    () => props.modelValue,
    newValue => {
      if (newValue !== localAmountValue.value) {
        localAmountValue.value = newValue;
      }
    }
  );

  watch(
    () => localAmountValue.value,
    newValue => {
      emit('update:modelValue', newValue);
    }
  );
</script>

<style lang="scss" src="~/assets/styles/components/form/input/amount-withdrawal.scss" />
